import { TS_REGEX } from './constants';
import { METADATA_LINE_REGEX } from './metadata';
import type { CaseMode } from './types';

/**
 * Normalizes curved "smart" quotes, curly apostrophes, dashes, and ellipses
 * into standard ASCII characters for universal media player compatibility.
 *
 * @param text Raw lyric line or full LRC text.
 * @returns Cleaned text and replacement count.
 */
export function cleanSmartQuotesAndPunctuation(text: string): {
  text: string;
  changesCount: number;
} {
  if (!text) return { text: '', changesCount: 0 };

  let count = 0;

  // Double quotes: “ ” „ ‟ « »
  const doubleQuotesRegex = /[“”„‟«»]/g;
  // Single quotes & apostrophes: ‘ ’ ‚ ‛ ′ ‵
  const singleQuotesRegex = /[‘’‚‛′‵]/g;
  // Dashes: em-dash (—), en-dash (–), horizontal bar (―), figure dash (‒)
  const dashesRegex = /[—–―‒]/g;
  // Ellipses character (… \u2026)
  const ellipsisRegex = /…/g;
  // Non-breaking and soft hyphens
  const weirdHyphenRegex = /[\u2011\u00AD]/g;
  // Decorative bullets
  const bulletsRegex = /[•·∙]/g;

  let cleaned = text;

  cleaned = cleaned.replace(doubleQuotesRegex, () => {
    count++;
    return '"';
  });

  cleaned = cleaned.replace(singleQuotesRegex, () => {
    count++;
    return "'";
  });

  cleaned = cleaned.replace(ellipsisRegex, () => {
    count++;
    return '...';
  });

  cleaned = cleaned.replace(weirdHyphenRegex, () => {
    count++;
    return '-';
  });

  // Em/En dashes: If between letters without space (e.g., "life—it's"), add friendly spacing " - "
  cleaned = cleaned.replace(/(\S)[—–―‒](\S)/g, (_m, p1, p2) => {
    count++;
    return `${p1} - ${p2}`;
  });

  // Remaining standalone dashes
  cleaned = cleaned.replace(dashesRegex, () => {
    count++;
    return '-';
  });

  cleaned = cleaned.replace(bulletsRegex, () => {
    count++;
    return '-';
  });

  return { text: cleaned, changesCount: count };
}

/**
 * Standard English minor words (articles, conjunctions, short prepositions)
 * that stay lowercase in Title Case unless at the beginning or end of a phrase.
 */
const TITLE_CASE_MINOR_WORDS = new Set([
  'a',
  'an',
  'the',
  'and',
  'but',
  'or',
  'nor',
  'for',
  'so',
  'yet',
  'as',
  'at',
  'by',
  'in',
  'of',
  'off',
  'on',
  'per',
  'to',
  'up',
  'via',
  'with'
]);

/**
 * Converts a single word to Title Case while handling punctuation, quotes,
 * contractions (e.g., Don't, It's), and hyphenated words.
 */
function toWordTitleCase(word: string, isFirstWord: boolean, isLastWord: boolean): string {
  if (!word) return '';

  // Extract leading punctuation (e.g. `(`, `"`, `[`)
  const leadingMatch = word.match(/^([^\p{L}\p{N}]+)/u);
  const leading = leadingMatch?.[1] ?? '';
  const coreWithTrailing = word.slice(leading.length);

  // Extract trailing punctuation (e.g. `)`, `"`, `!`, `,`)
  const trailingMatch = coreWithTrailing.match(/([^\p{L}\p{N}]+)$/u);
  const trailing = trailingMatch?.[1] ?? '';
  const core = coreWithTrailing.slice(0, coreWithTrailing.length - trailing.length);

  if (!core) return word;

  const lowerCore = core.toLowerCase();

  // If it's a minor word and not the first or last word in the phrase, keep lowercase
  if (!isFirstWord && !isLastWord && TITLE_CASE_MINOR_WORDS.has(lowerCore)) {
    return `${leading}${lowerCore}${trailing}`;
  }

  // Handle hyphenated compound words (e.g. "heart-to-heart" -> "Heart-to-Heart")
  if (core.includes('-')) {
    const parts = core.split('-');
    const titledParts = parts.map((part, idx) =>
      toWordTitleCase(
        part,
        idx === 0 ? isFirstWord : false,
        idx === parts.length - 1 ? isLastWord : false
      )
    );
    return `${leading}${titledParts.join('-')}${trailing}`;
  }

  // Handle English pronoun "I" or contractions starting with "I'" (e.g., "I'm", "I'll", "I'd", "I've")
  if (lowerCore === 'i' || lowerCore.startsWith("i'")) {
    const capitalized = 'I' + core.slice(1).toLowerCase();
    return `${leading}${capitalized}${trailing}`;
  }

  // Standard capitalization: first letter capitalized, rest lowercase
  const titledCore = core.charAt(0).toUpperCase() + core.slice(1).toLowerCase();

  return `${leading}${titledCore}${trailing}`;
}

/**
 * Converts lyric text to Title Case.
 */
export function toLyricTitleCase(text: string): string {
  if (!text) return '';

  // Split into tokens preserving whitespace
  const tokens = text.split(/(\s+)/);
  const wordsOnly = tokens.filter((t) => t.trim().length > 0);
  const totalWords = wordsOnly.length;
  let wordIndex = 0;

  return tokens
    .map((token) => {
      if (!token.trim()) return token; // whitespace delimiter

      const isFirst = wordIndex === 0;
      const isLast = wordIndex === totalWords - 1;
      wordIndex++;

      return toWordTitleCase(token, isFirst, isLast);
    })
    .join('');
}

/**
 * Converts lyric text to Sentence case (capitalizes only the first letter of the sentence/line).
 */
export function toLyricSentenceCase(text: string): string {
  if (!text) return '';

  const trimmed = text.trim();
  if (!trimmed) return text;

  // Find the first alphabetical character, skipping leading punctuation/quotes/brackets
  const firstLetterMatch = text.match(/[\p{L}]/u);
  if (!firstLetterMatch || firstLetterMatch.index === undefined) {
    return text.toLowerCase();
  }

  const idx = firstLetterMatch.index;
  const prefix = text.slice(0, idx);
  const firstChar = text.charAt(idx).toUpperCase();
  const rest = text.slice(idx + 1).toLowerCase();

  // Special case: preserve uppercase "I" in English contractions: "i'm" -> "I'm", " i " -> " I "
  const formattedRest = rest
    .replace(/\bi\b/g, 'I')
    .replace(/\bi'([a-z]+)/g, "I'$1");

  return `${prefix}${firstChar}${formattedRest}`;
}

/**
 * Normalizes letter case across LRC lyrics while strictly preserving:
 * 1. Timestamps (e.g., [00:12.34], [01:00.00])
 * 2. Standard ID metadata tags (e.g., [ti:Song Title], [ar:Artist])
 *
 * @param rawText Full raw LRC text.
 * @param mode Target case format ('sentence' | 'title' | 'lower' | 'upper').
 */
export function normalizeLetterCase(rawText: string, mode: CaseMode): string {
  if (!rawText) return '';

  const lines = rawText.split(/\r?\n/);
  const formattedLines: string[] = [];

  for (const rawLine of lines) {
    const trimmed = rawLine.trim();

    // Preserve empty lines
    if (!trimmed) {
      formattedLines.push(rawLine);
      continue;
    }

    // Preserve metadata header lines exactly without modifying their case
    if (!TS_REGEX.test(trimmed) && METADATA_LINE_REGEX.test(trimmed)) {
      TS_REGEX.lastIndex = 0;
      formattedLines.push(rawLine);
      continue;
    }
    TS_REGEX.lastIndex = 0;

    // Check if line contains LRC timestamp brackets
    const matches = rawLine.match(TS_REGEX);
    if (matches && matches.length > 0) {
      // Find where timestamps end and lyric text begins
      const lastMatch = matches[matches.length - 1];
      if (!lastMatch) {
        formattedLines.push(rawLine);
        continue;
      }
      const lastMatchIdx = rawLine.lastIndexOf(lastMatch);
      const tsPrefixEnd = lastMatchIdx + lastMatch.length;

      const tsPrefix = rawLine.slice(0, tsPrefixEnd);
      const lyricPart = rawLine.slice(tsPrefixEnd);

      // Separate any leading spacing after timestamp
      const spacingMatch = lyricPart.match(/^(\s*)/);
      const spacer = spacingMatch?.[1] ?? '';
      const actualLyric = lyricPart.slice(spacer.length);

      let transformedLyric = actualLyric;
      switch (mode) {
        case 'sentence':
          transformedLyric = toLyricSentenceCase(actualLyric);
          break;
        case 'title':
          transformedLyric = toLyricTitleCase(actualLyric);
          break;
        case 'lower':
          transformedLyric = actualLyric.toLowerCase();
          break;
        case 'upper':
          transformedLyric = actualLyric.toUpperCase();
          break;
      }

      formattedLines.push(`${tsPrefix}${spacer}${transformedLyric}`);
    } else {
      // Plain text lyric line without timestamps
      let transformed = rawLine;
      switch (mode) {
        case 'sentence':
          transformed = toLyricSentenceCase(rawLine);
          break;
        case 'title':
          transformed = toLyricTitleCase(rawLine);
          break;
        case 'lower':
          transformed = rawLine.toLowerCase();
          break;
        case 'upper':
          transformed = rawLine.toUpperCase();
          break;
      }
      formattedLines.push(transformed);
    }
  }

  return formattedLines.join('\n');
}
